import {
  createApi,
  fetchBaseQuery,
  FetchBaseQueryError,
  FetchArgs,
  BaseQueryFn
} from "@reduxjs/toolkit/query/react";

interface IResponse {
  StatusCode: number;
  Status: string;
  TotalRecords: number;
  Message?: string;
  Model: { [key: string]: any }[];
  Value?: string;
}

const token = localStorage.getItem("accessToken");

const baseQuery = fetchBaseQuery({
  baseUrl: process.env.REACT_APP_SMARTPRINT_API,
  prepareHeaders: (headers: any, { getState, endpoint }) => {
    if (token) {
      headers.set("Authorization", `Bearer ${token}`);
    }
    headers.set("X-Frame-Options", "SAMEORIGIN");
    headers.set("X-Content-Type-Options", "nosniff");
    headers.set(
      "Strict-Transport-Security",
      "max-age=31536000; includeSubDomains; preload"
    );
    headers.set("Referrer-Policy", "strict-origin-when-cross-origin");
    headers.set(
      "Content-Security-Policy",
      "frame-ancestors 'self'; upgrade-insecure-requests;"
    );
    return headers;
  }
});

const fetchToken = async () => {
  const response = await fetch(
    `${process.env.REACT_APP_SMARTPRINT_API}locations/GetAntiForgeryToken`,
    {
      credentials: "include",
      headers: {
        Authorization: `Bearer ${token}`
      }
    }
  );
  const text = await response.text();
  const data = JSON.parse(text);
  return data.token;
};

const baseQueryWithReauth: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args: any, api: any, extraOptions: any) => {
  if (typeof args !== "string" && args.method === "POST") {
    const token = await fetchToken();
    if (token) {
      if (!args.headers) {
        args.headers = {};
      }
      if (args.headers instanceof Headers) {
        args.headers.set("X-CSRF-TOKEN", token);
      } else if (Array.isArray(args.headers)) {
        args.headers.push(["X-CSRF-TOKEN", token]);
      } else {
        args.headers["X-CSRF-TOKEN"] = token;
      }
    }
  }

  let result: any = await baseQuery(
    {
      ...args,
      credentials: "include"
    },
    api,
    extraOptions
  );
  console.log("Request result:", result);
  if (result && result?.error) {
    if (result.error.status === 401) {
      console.log("Session expired");
      localStorage.setItem("isSessionExpired", "true");
    } else {
      localStorage.setItem("isSessionExpired", "false");
    }
  } else if (!result) {
    console.log("API is still loading");
    localStorage.setItem("isSessionExpired", "false");
  } else {
    localStorage.setItem("isSessionExpired", "false");
  }

  return result;
};

export const myApi = createApi({
  reducerPath: "myApi",
  baseQuery: baseQueryWithReauth,
  tagTypes: ["Posts"],
  endpoints: (builder) => ({
    getPosts: builder.query<IResponse, string>({
      query: (urlname) => ({
        url: urlname,
        method: "GET"
      })
    }),
    newPost: builder.mutation<
      IResponse,
      { Method: string; [key: string]: any }
    >({
      query: (newData) => {
        const { Method, ...data } = newData;
        return {
          url: `/${Method}`,
          method: "POST",
          body: data,
          headers: {
            "Content-Type": "application/json"
          }
        };
      },
      invalidatesTags: ["Posts"]
    }),
    putData: builder.mutation<
      IResponse,
      { Method: string; [key: string]: any }
    >({
      query: (newData) => {
        const { Method, ...data } = newData;
        return {
          url: `/${Method}`,
          method: "PUT",
          body: data,
          headers: {
            "Content-Type": "application/json;charset=UTF-8"
          }
        };
      },
      invalidatesTags: ["Posts"]
    }),
    patchData: builder.mutation<
      IResponse,
      { Method: string; [key: string]: any }
    >({
      query: (newData) => {
        const { Method, ...data } = newData;
        return {
          url: `/${Method}`,
          method: "PATCH",
          body: data,
          headers: {
            "Content-Type": "application/json"
          }
        };
      },
      invalidatesTags: ["Posts"]
    })
  })
});

export const {
  useGetPostsQuery,
  useNewPostMutation,
  usePutDataMutation,
  usePatchDataMutation
} = myApi;
